import { useState, useEffect, useContext } from 'react'
import { PageContext, ThemeContext, UserContext } from '../Context';
import GetUserAvatar from '../tool/GetUserAvatar';
import GetUserField from '../tool/GetUserField';
import { 
  Box,
  Paper,
  Typography, 
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  Button
} from '@mui/material';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import { Menu as MenuIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon, LightMode as LightModeIcon, DarkMode as DarkModeIcon, ManageAccounts as ManageAccountIcon, Edit as EditIcon, CopyAll as CopyAllIcon, Folder as FolderIcon, Bookmark as ArchiveIcon, History as HistoryIcon, Search as SearchIcon, Add as AddIcon, Logout as LogoutIcon, Home as HomeIcon, MenuBook as MenuBookIcon, Delete as DeleteIcon, Download as DownloadIcon, FileCopy as FileCopyIcon, LibraryBooks as LibraryBooksIcon, FileUpload as FileUploadIcon, Campaign as CampaignIcon, PhotoCamera as PhotoCameraIcon, AudioFile as AudioFileIcon, Summarize as SummarizeIcon } from '@mui/icons-material';

function Tutorial() {
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const { setPage } = useContext(PageContext);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [matricule, setMatricule] = useState(null);
  
  // Styles adaptatifs pour le thème
  const isDark = theme === 'dark';
  const contrastOnPrimary = muiTheme.palette.getContrastText(muiTheme.palette.primary.main);

  // Structure du sommaire
  const tableOfContents = [
    {
      id: 'configuration',
      title: 'Configurer mon compte',
      subsections: [
        { id: 'affichage', title: 'Changer le mode d\'affichage' },
        { id: 'mot-de-passe', title: 'Changer mon mot de passe' },
        { id: 'couleur-nom', title: 'Changer la couleur de mon nom' },
        { id: 'insertions-perso', title: 'Créer, modifier et supprimer mes insertions personnelles' },
        { id: 'rubriques-perso', title: 'Créer, modifier et supprimer mes rubriques personnelles' }
      ]
    },
    {
      id: 'pv',
      title: 'Créer et charger un document',
      subsections: [
        { id: 'tableau-bord', title: 'Utiliser mon tableau de bord' },
        { id: 'charger-pv', title: 'Charger un document' },
        { id: 'creer-pv', title: 'Créer un nouveau document' }
      ]
    },
    {
      id: 'redaction',
      title: 'Rédiger mon procès-verbal',
      subsections: [
        { id: 'quitter-pv', title: 'Utiliser le bouton Maison' },
        { id: 'modes', title: 'Les modes "Édition" et "Lecture"' },
        { id: 'encodage', title: 'Etablir l\'encodage' },
        { id: 'reprise-import-islp', title: 'Reprendre ou importer des encodages' },
        { id: 'ajouter-rubrique', title: 'Ajouter une rubrique' },
        { id: 'rubriques', title: 'Utiliser mes rubriques personnelles' },
        { id: 'deplacer-rubrique', title: 'Déplacer une rubrique' },
        { id: 'supprimer-rubrique', title: 'Supprimer une rubrique' },
        { id: 'generer-rubrique', title: 'Générer le contenu d\'une rubrique' },
        { id: 'modifier-rubrique', title: 'Modifier le texte et le contenu d\'une rubrique' },
        { id: 'commenter-rubrique', title: 'Commenter une rubrique' },
        { id: 'historique', title: 'Consulter l\'historique' },
        { id: 'correcteur', title: 'Utiliser le correcteur orthographique' },
        { id: 'discussion', title: 'Utiliser la discussion' },
        { id: 'guardia', title: 'Utiliser GuardIA' },
        { id: 'insertions', title: 'Utiliser mes insertions personnelles' },
        { id: 'annexes', title: 'Générer une annexe' },
        { id: 'sauvegarder', title: 'Sauvegarder mon procès-verbal' },
        { id: 'supprimer-pv', title: 'Supprimer un procès-verbal' },
        { id: 'exporter', title: 'Exporter mon procès-verbal' }
      ]
    },
    {
      id: 'renseignements',
      title: 'Renseignements complémentaires',
      subsections: [
        { id: 'deconnexion', title: 'Se déconnecter' },
        { id: 'securite', title: 'Sécurité' },
        { id: 'contact', title: 'Contact et signalement' },
        { id: 'version', title: 'Version de l\'application' },
        { id: 'conditions', title: 'Conditions d\'utilisation et mentions légales' }
      ]
    }
  ];

  // Fonction pour naviguer vers une section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
      if (isMobile) {
        setDrawerOpen(false);
      }
    }
  };

  // Observer pour détecter la section active
  useEffect(() => {
    const observerOptions = {
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    // Observer toutes les sections
    const sections = document.querySelectorAll('[id]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Composant du sommaire
  const TableOfContentsContent = () => (
    <Box sx={{ width: 280, p: 2 }}>
      <List dense>
        {tableOfContents.map((section) => (
          <Box key={section.id}>
            <ListItem
              button
              onClick={() => scrollToSection(section.id)}
              sx={{
                pl: 1,
                py: 0.5,
                borderRadius: 1,
                mb: 0.5,
                backgroundColor: activeSection === section.id 
                  ? (isDark ? 'rgba(144, 202, 249, 0.1)' : 'rgba(25, 118, 210, 0.1)')
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <ListItemText 
                primary={section.title}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.9rem',
                    fontWeight: activeSection === section.id ? 'bold' : 'normal',
                    color: activeSection === section.id 
                      ? (isDark ? '#90caf9' : 'primary.main')
                      : 'inherit'
                  }
                }}
              />
            </ListItem>
            {section.subsections.map((subsection) => (
              <ListItem
                key={subsection.id}
                button
                onClick={() => scrollToSection(subsection.id)}
                sx={{
                  pl: 3,
                  py: 0.25,
                  borderRadius: 1,
                  mb: 0.25,
                  backgroundColor: activeSection === subsection.id 
                    ? (isDark ? 'rgba(144, 202, 249, 0.1)' : 'rgba(25, 118, 210, 0.1)')
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ListItemText 
                  primary={subsection.title}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '0.8rem',
                      fontWeight: activeSection === subsection.id ? 'bold' : 'normal',
                      color: activeSection === subsection.id 
                        ? (isDark ? '#90caf9' : 'primary.main')
                        : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')
                    }
                  }}
                />
              </ListItem>
            ))}
          </Box>
        ))}
      </List>
    </Box>
  );

   return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      gap: 3,
      p: 3,
      minHeight: '100vh'
    }}>
      {/* Sommaire pour desktop */}
      {!isMobile && (
        <Box
          sx={{
            width: 300,
            height: 'fit-content',
            position: 'sticky',
            top: 'calc(6.5vh + 20px)',
            maxHeight: 'calc(100vh - 6.5vh - 40px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Box
            component={Paper}
            elevation={2}
            sx={{
              p: 1.5,
              flexShrink: 0,
              backgroundColor: isDark ? '#233044' : '#ffffff',
              color: isDark ? '#fff' : 'inherit',
              boxShadow: isDark 
                ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                : '0 4px 20px rgba(0,0,0,0.05)',
              borderRadius: 2
            }}
          >
            <Button
              fullWidth
              startIcon={<ArrowBackIcon />}
              onClick={() => setPage('Edition')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: isDark ? '#90caf9' : 'primary.main',
                justifyContent: 'flex-start'
              }}
            >
              Retour à l'espace « Rédaction »
            </Button>
          </Box>
          <Box
            component={Paper}
            elevation={2}
            sx={{
              backgroundColor: isDark ? '#233044' : '#ffffff',
              color: isDark ? '#fff' : 'inherit',
              boxShadow: isDark 
                ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                : '0 4px 20px rgba(0,0,0,0.05)',
              borderRadius: 2,
              overflowY: 'auto'
            }}
          >
            <TableOfContentsContent />
          </Box>
        </Box>
      )}

      {/* Bouton menu pour mobile */}
      {isMobile && (
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{
            position: 'fixed',
            top: 'calc(6.5vh + 16px)',
            left: 16,
            zIndex: 1100,
            backgroundColor: isDark ? '#233044' : '#ffffff',
            color: isDark ? '#90caf9' : 'primary.main',
            boxShadow: isDark 
              ? '0 4px 20px rgba(0, 0, 0, 0.5)'
              : '0 4px 20px rgba(0,0,0,0.1)',
            '&:hover': {
              backgroundColor: isDark ? '#2a3e57' : '#f5f5f5'
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Drawer pour mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
            color: isDark ? '#fff' : 'inherit'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setPage('Edition')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              color: isDark ? '#90caf9' : 'primary.main'
            }}
          >
            Retour à l'espace « Rédaction »
          </Button>
          <IconButton 
            onClick={() => setDrawerOpen(false)}
            sx={{ color: isDark ? '#fff' : 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <TableOfContentsContent />
      </Drawer>

      {/* Contenu principal */}
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          maxWidth: 900,
          width: '100%',
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
          boxShadow: isDark 
            ? '0 4px 20px rgba(0, 0, 0, 0.5)'
            : '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
      <CardContent>
        <Box sx={{ 
          textAlign: 'center', 
          mb: 5,
          p: 4,
          borderRadius: 3,
          background: isDark 
            ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#ffffff',
          boxShadow: isDark
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(102, 126, 234, 0.2)'
        }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              mb: 2,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Guide d'utilisation de BlueNote
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              opacity: 0.9,
              fontWeight: 400,
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}
          >
            Découvrez les fonctionnalités de l'application pas à pas
          </Typography>
        </Box>

        <Box id="configuration" sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: isDark ? '#90caf9' : 'primary.main', mb: 3 }}>
            Configurer mon compte
          </Typography>
          
          <Box id="affichage" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Changer le mode d'affichage
            </Typography>
            <Typography variant="body1" paragraph>
              L'application BlueNote propose deux modes d'affichage : un thème clair et un thème foncé. Cette double option permet d'adapter l'interface à vos préférences visuelles, que ce soit en journée, la nuit ou lors de longues sessions de rédaction.
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez changer de mode d'affichage à tout moment grâce au bouton situé en haut à droite de votre fenêtre.
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Cliquez sur l'icône <LightModeIcon sx={{ fontSize: '1.5rem' }} /> pour activer le <strong>thème clair</strong>.
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Cliquez sur l'icône <DarkModeIcon sx={{ fontSize: '1.5rem' }} /> pour activer le <strong>thème foncé</strong>.
            </Typography>
            <Typography variant="body1" paragraph>
              Votre préférence est enregistrée automatiquement et sera appliquée lors de votre prochaine connexion.
            </Typography>
          </Box>

          <Box id="mot-de-passe" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Changer mon mot de passe
            </Typography>
            <Typography variant="body1" paragraph>
              Lors de votre première connexion, vous avez utilisé un mot de passe fourni par votre zone. Il est essentiel de le modifier immédiatement afin d'être le seul à pouvoir accéder à votre compte.
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Pour ce faire, cliquez sur le menu déroulant situé dans l'en-tête de l'application, puis rendez-vous sur la page 
              <Chip 
                icon={<ManageAccountIcon />}
                label="Mon compte"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#FF9800',
                  fontWeight: 500,
                  border: '1px solid #FF9800',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#FF9800'
                  }
                }}
              />
              . Dans la section « <strong>Modifier mon mot de passe</strong> », entrez votre ancien mot de passe ainsi que le nouveau, puis validez. Veillez à choisir un mot de passe sécurisé, unique (non utilisé sur d'autres services), et, de préférence, composé d'une combinaison de lettres majuscules et minuscules, de chiffres et de caractères spéciaux.
            </Typography>
          </Box>

          <Box id="couleur-nom" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Changer la couleur de mon nom
            </Typography>
            <Typography variant="body1" paragraph>
              Pour les autres utilisateurs, votre nom est accompagné d'une bulle colorée afin de faciliter votre identification. Voici comment vous apparaissez aux autres :
            </Typography>
            <GetUserField fieldName="matricule" onLoad={setMatricule} />
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 2, 
              bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: 2,
              border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
              mb: 2
            }}>
              {matricule && <GetUserAvatar matricule={matricule} defaultValue="..." />}
            </Box>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Pour choisir votre couleur, cliquez sur le menu déroulant situé dans l'en-tête de l'application, puis rendez-vous sur la page 
              <Chip 
                icon={<ManageAccountIcon />}
                label="Mon compte"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#FF9800',
                  fontWeight: 500,
                  border: '1px solid #FF9800',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#FF9800'
                  }
                }}
              />
              . Dans la section « <strong>Couleur de mon nom</strong> », sélectionnez la couleur désirée et validez.
            </Typography>
          </Box>

          <Box id="insertions-perso" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Créer, modifier et supprimer mes insertions personnelles
            </Typography>
            <Typography variant="body1" paragraph>
              L'application BlueNote vous permet, comme dans les logiciels de traitement de texte, de créer des insertions automatiques : des phrases ou rubriques que vous utilisez fréquemment, prêtes à être insérées en un clic.
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Pour les gérer, cliquez sur le menu déroulant situé dans l'en-tête de l'application, puis rendez-vous sur la page 
              <Chip 
                icon={<CopyAllIcon />}
                label="Mes insertions"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#2196F3',
                  fontWeight: 500,
                  border: '1px solid #2196F3',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#2196F3'
                  }
                }}
              />
              . Vous y trouverez un formulaire vous permettant de créer, modifier ou supprimer vos insertions personnelles.
            </Typography>
            <Typography variant="body1" paragraph>
              Ces insertions pourront ensuite être utilisées directement lors de la rédaction d'un procès-verbal. Pour en savoir plus, consultez la section «{' '}
              <Typography 
                component="span" 
                sx={{ 
                  color: isDark ? '#90caf9' : 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
                onClick={() => scrollToSection('insertions')}
              >
                Utiliser mes insertions personnelles
              </Typography>
              {' '}».
            </Typography>
          </Box>

          <Box id="rubriques-perso" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Créer, modifier et supprimer mes rubriques personnelles
            </Typography>
            <Typography variant="body1" paragraph>
              En complément des canevas de rubriques mis à disposition par l'application, BlueNote vous permet de créer vos propres rubriques personnelles : des modèles de rubriques que vous utilisez fréquemment, prêts à être ajoutés à un procès-verbal en un clic.
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Pour les gérer, cliquez sur le menu déroulant situé dans l'en-tête de l'application, puis rendez-vous sur la page 
              <Chip 
                icon={<LibraryBooksIcon />}
                label="Mes rubriques"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#9C27B0',
                  fontWeight: 500,
                  border: '1px solid #9C27B0',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#9C27B0'
                  }
                }}
              />
              . Vous y trouverez un formulaire vous permettant de créer, modifier ou supprimer vos rubriques personnelles.
            </Typography>
            <Typography variant="body1" paragraph>
              Ces rubriques pourront ensuite être utilisées directement lors de la rédaction d'un procès-verbal. Pour en savoir plus, consultez la section «{' '}
              <Typography 
                component="span" 
                sx={{ 
                  color: isDark ? '#90caf9' : 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
                onClick={() => scrollToSection('rubriques')}
              >
                Utiliser mes rubriques personnelles
              </Typography>
              {' '}».
            </Typography>
          </Box>

          <Box id="statistiques" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Statistiques
            </Typography>
            <Typography variant="body1" paragraph>
              Cette section vous propose des statistiques individuelles à but informatif, afin de disposer d'une vue globale sur votre utilisation de l'application.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box id="pv" sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: isDark ? '#90caf9' : 'primary.main', mb: 3 }}>
            Créer et charger un document
          </Typography>
          
          <Box id="tableau-bord" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Utiliser mon tableau de bord
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Pour accéder au tableau de bord, cliquez sur le menu déroulant situé dans l'en-tête de l'application, puis sélectionnez la page 
              <Chip 
                icon={<EditIcon />}
                label="Rédaction"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#4CAF50',
                  fontWeight: 500,
                  border: '1px solid #4CAF50',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#4CAF50'
                  }
                }}
              />
              .
            </Typography>
            <Typography variant="body1" paragraph>
              Le tableau de bord est structuré en trois onglets distincts :
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon sx={{ fontSize: '1.5rem' }} /> Mes dossiers
            </Typography>
            <Typography variant="body1" paragraph>
              Affiche les procès-verbaux que vous consultez actuellement et qui sont en cours de rédaction. Cette vue vous permet de retrouver rapidement vos PV en cours de travail.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArchiveIcon sx={{ fontSize: '1.5rem' }} /> Mes archives
            </Typography>
            <Typography variant="body1" paragraph>
              Contient les procès-verbaux que vous avez archivés une fois terminés. Ils restent accessibles mais n'encombrent plus votre vue principale.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ fontSize: '1.5rem' }} /> Derniers PV
            </Typography>
            <Typography variant="body1" paragraph>
              Affiche les tout derniers procès-verbaux créés par tous les utilisateurs de votre zone. Pratique pour retrouver un dossier récent créé par un collègue.
            </Typography>
            <Typography variant="body1" paragraph>
              Lorsque vous avez terminé un procès-verbal dans l'onglet <FolderIcon sx={{ fontSize: '1rem' }} /> <strong>Mes dossiers</strong>, cliquez sur l'icône <ArchiveIcon sx={{ fontSize: '1.2rem' }} /> pour le déplacer vers <ArchiveIcon sx={{ fontSize: '1rem' }} /> <strong>Mes archives</strong>. Vous pourrez le rouvrir à tout moment ; il réapparaîtra alors automatiquement dans vos dossiers en cours.
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                💡
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Astuce
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Archivez vos dossiers dès qu'ils sont finalisés afin de garder une vue claire et organisée de votre travail en cours.
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" paragraph>
              Pour ouvrir rapidement un procès-verbal depuis l'une des listes, cliquez simplement sur l'icône <EditIcon sx={{ fontSize: '1.2rem' }} />.
            </Typography>
          </Box>

          <Box id="charger-pv" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Charger un procès-verbal
            </Typography>
            <Typography variant="body1" paragraph>
              Vous disposez de deux moyens pour ouvrir un procès-verbal dans l'application :
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Via la barre de recherche
            </Typography>
            <Typography variant="body1" paragraph>
              À tout moment, vous pouvez charger un procès-verbal en utilisant la barre de recherche située dans l'en-tête de l'application.
            </Typography>
            <Typography variant="body1" paragraph>
              Saisissez les six chiffres et l'année du procès-verbal (<i>par exemple : 123456/{new Date().getFullYear()}</i>), puis cliquez sur l'icône <SearchIcon sx={{ fontSize: '1.2rem' }} />.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Depuis le tableau de bord
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Si le procès-verbal figure parmi vos dossiers en cours ou fait partie des derniers procès-verbaux créés, vous pouvez également y accéder directement depuis la page 
              <Chip 
                icon={<EditIcon />}
                label="Rédaction"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#4CAF50',
                  fontWeight: 500,
                  border: '1px solid #4CAF50',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#4CAF50'
                  }
                }}
              />
              , comme expliqué dans la section «{' '}
              <Typography 
                component="span" 
                sx={{ 
                  color: isDark ? '#90caf9' : 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
                onClick={() => scrollToSection('tableau-bord')}
              >
                Utiliser mon tableau de bord
              </Typography>
              {' '}».
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Accès refusé
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4,
                  mb: 1
                }}>
                  Si l'ouverture du procès-verbal vous est interdite, cela signifie que son créateur a restreint l'accès et que vous n'avez pas été ajouté comme personne autorisée.
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Vous pensez avoir un droit légitime d'accès ? Contactez le gestionnaire de l'application afin qu'il puisse ajuster les autorisations.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box id="creer-pv" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Créer un nouveau document
            </Typography>
            <Typography variant="body1" paragraph>
              Deux méthodes s'offrent à vous pour créer un nouveau document dans l'application :
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Depuis le tableau de bord
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Cliquez sur le menu déroulant dans l'en-tête de l'application et rendez-vous sur la page 
              <Chip 
                icon={<EditIcon />}
                label="Rédaction"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#4CAF50',
                  fontWeight: 500,
                  border: '1px solid #4CAF50',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#4CAF50'
                  }
                }}
              />
              . Sur votre tableau de bord, cliquez sur ce bouton pour lancer la création :
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  opacity: 0.8,
                  cursor: 'not-allowed'
                }}
              >
                Nouveau document
              </Button>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Via la barre de recherche
            </Typography>
            <Typography variant="body1" paragraph>
              À tout moment, vous pouvez créer un document en saisissant les six chiffres et l'année du procès-verbal dans la barre de recherche située en haut de l'écran, puis en cliquant sur l'icône <SearchIcon sx={{ fontSize: '1.2rem' }} />.
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Attention
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Assurez-vous que le numéro entré ne correspond pas à un document déjà existant. Si tel est le cas, vous serez redirigé vers celui-ci au lieu d'en créer un nouveau.
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" paragraph>
              Dans les deux cas, vous serez invité à renseigner les informations suivantes :
            </Typography>
            <Typography variant="body1" paragraph>
              Le type de procès-verbal soit initial ou subséquent.
            </Typography>
            <Typography variant="body1" paragraph>
              Son indice éventuel, son numéro, et l'année.
            </Typography>
            <Typography variant="body1" paragraph>
              Vous avez également la possibilité de <strong>restreindre l'accès</strong> au document. Si cette option est activée, vous devrez indiquer la liste des personnes autorisées à y accéder.
            </Typography>
            <Typography variant="body1" paragraph>
              Il est possible que votre administrateur ait paramétré la confidentialité afin que les documents soient d'office restreints.
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Note importante
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Les utilisateurs ayant la qualité de modérateur ainsi que le gestionnaire de l'application conserveront dans tous les cas un droit d'accès au procès-verbal.
                </Typography>
              </Box>
            </Box>
          </Box>


        </Box>

        <Divider sx={{ my: 4 }} />

        <Box id="redaction" sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: isDark ? '#90caf9' : 'primary.main', mb: 3 }}>
            Rédiger mon procès-verbal
          </Typography>
          
          <Box id="quitter-pv" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Utiliser le bouton "Maison"
            </Typography>
            <Typography variant="body1" paragraph>
              Le bouton Maison est situé tout à gauche de la barre supérieure du document :
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDark ? '#4fc3f7' : 'info.main',
                  color: 'white',
                  borderRadius: 1,
                  opacity: 0.7,
                  cursor: 'not-allowed'
                }}
              >
                <HomeIcon sx={{ fontSize: 31 }} />
              </Box>
            </Box>
            <Typography variant="body1" paragraph>
              Cliquez dessus pour quitter le document et revenir au tableau de bord.
            </Typography>
            <Typography variant="body1" paragraph>
              En le survolant, un panneau d'accès rapide au tableau de bord s'ouvre. Il vous permet de changer rapidement vers un autre document récent ou d'en créer un nouveau, tout en gardant la possibilité de quitter le document en cours d'un simple clic.
            </Typography>
          </Box>

          <Box id="modes" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Les modes "Édition" et "Lecture"
            </Typography>
            <Typography variant="body1" paragraph>
              BlueNote vous propose deux modes d'affichage pour visualiser et travailler sur un procès-verbal.
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez passer de l'un à l'autre à l'aide du sélecteur situé au centre de la barre supérieure de l'application :
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '4px',
                  width: { xs: '78vw', sm: '440px' },
                  maxWidth: '100%',
                  borderRadius: '999px',
                  px: '4px',
                  py: '3px',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.08)'}`,
                  backdropFilter: 'saturate(160%) blur(8px)',
                  boxShadow: isDark 
                    ? '0 8px 24px rgba(0,0,0,0.35)'
                    : '0 6px 18px rgba(0,0,0,0.12)',
                  opacity: 0.7,
                  pointerEvents: 'none'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 3,
                    bottom: 3,
                    left: 4,
                    width: 'calc((100% - 8px) / 2)',
                    borderRadius: '999px',
                    background: `linear-gradient(135deg, ${muiTheme.palette.primary.main}, ${muiTheme.palette.primary.dark})`,
                    boxShadow: isDark
                      ? '0 8px 20px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.08)'
                      : '0 6px 16px rgba(25,118,210,0.25)'
                  }}
                />
                <Button
                  startIcon={<EditIcon />}
                  sx={{
                    zIndex: 1,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    height: '34px',
                    borderRadius: '999px',
                    color: contrastOnPrimary,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: 'transparent' }
                  }}
                >
                  Édition
                </Button>
                <Button
                  startIcon={<MenuBookIcon />}
                  sx={{
                    zIndex: 1,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    height: '34px',
                    borderRadius: '999px',
                    color: isDark ? '#c9d1d9' : muiTheme.palette.text.secondary,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: 'transparent' }
                  }}
                >
                  Lecture
                </Button>
              </Box>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            {<EditIcon />} Mode Édition
            </Typography>
            <Typography variant="body1" paragraph>
              Ce mode affiche le procès-verbal sous forme de rubriques séparées, facilitant la saisie et la modification.
            </Typography>
            <Typography variant="body1" paragraph>
              Il est conçu pour vous permettre de modifier librement le contenu de chaque rubrique.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            {<MenuBookIcon />} Mode Lecture
            </Typography>
            <Typography variant="body1" paragraph>
              Ce mode propose une prévisualisation fidèle du procès-verbal tel qu'il apparaîtra une fois finalisé.
            </Typography>
            <Typography variant="body1" paragraph>
              Les rubriques sont affichées de manière continue, sans possibilité d'édition.
            </Typography>
            <Typography variant="body1" paragraph>
              C'est le mode idéal pour une relecture ou une vérification du rendu final.
            </Typography>
          </Box>

          <Box id="encodage" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Procéder à l'encodage
            </Typography>
            <Typography variant="body1" paragraph>
              Avant de poursuivre la création de votre procès-verbal, il est essentiel d'effectuer l'encodage des éléments clés.
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Pour ce faire, rendez-vous dans l'onglet 
              <Chip 
                label="Encodage"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  fontWeight: 500,
                  border: `1px solid ${isDark ? '#4fc3f7' : '#1976d2'}`,
                  mx: 0.5,
                }}
              />
              , situé dans la colonne de gauche de l'application.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez ensuite sur ces boutons pour chaque section suivante :
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
              <Button
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  borderColor: isDark ? '#4fc3f7' : '#1976d2',
                  py: 0.25,
                  px: 0.75,
                  minHeight: '24px',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                  '&.Mui-disabled': {
                    color: isDark ? '#4fc3f7' : '#1976d2',
                    borderColor: isDark ? '#4fc3f7' : '#1976d2',
                    opacity: 0.7
                  }
                }}
              >
                Modifier
              </Button>
              <Button
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  borderColor: isDark ? '#4fc3f7' : '#1976d2',
                  py: 0.25,
                  px: 0.75,
                  minHeight: '24px',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                  '&.Mui-disabled': {
                    color: isDark ? '#4fc3f7' : '#1976d2',
                    borderColor: isDark ? '#4fc3f7' : '#1976d2',
                    opacity: 0.7
                  }
                }}
              >
                Ajouter
              </Button>
            </Box>
            <Typography variant="body1" paragraph>
              <strong>Structure</strong> : Permet de modifier l'indice du procès-verbal.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Rédaction</strong> : Vous permet de renseigner :
            </Typography>
            <Typography variant="body1" paragraph>
              La date et l'heure de début de la rédaction
            </Typography>
            <Typography variant="body1" paragraph>
              La date et l'heure de clôture du procès-verbal
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Service</strong> : Identifiez le service chargé de la rédaction.
            </Typography>
            <Typography variant="body1" paragraph>
              Cette information est automatiquement préremplie selon le service de l'utilisateur ayant créé le procès-verbal.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Intervenants</strong> : Encodez tous les intervenants présents dans l'intervention, y compris :
            </Typography>
            <Typography variant="body1" paragraph>
              Les collègues impliqués
            </Typography>
            <Typography variant="body1" paragraph>
              Les supérieurs hiérarchiques avisés
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Personnes concernées</strong> : Renseignez toutes les personnes physiques citées dans le corps du procès-verbal.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Objets
            </Typography>
            <Typography variant="body1" paragraph>
              Encodez les objets mentionnés dans des rubriques telles que :
            </Typography>
            <Typography variant="body1" paragraph>
              Préjudice
            </Typography>
            <Typography variant="body1" paragraph>
              Saisie
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Véhicules
            </Typography>
            <Typography variant="body1" paragraph>
              Renseignez les véhicules impliqués dans les faits.
            </Typography>
          </Box>

          <Box id="reprise-import-islp" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Reprendre ou importer des encodages
            </Typography>
            <Typography variant="body1" paragraph>
              En bas de l'onglet « Encodage », deux boutons vous permettent de gagner du temps lorsque les informations existent déjà ailleurs :
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
              <Button
                variant="text"
                startIcon={<FileCopyIcon />}
                sx={{
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: isDark ? '#4fc3f7' : '#1565c0',
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(21, 101, 192, 0.08)',
                  border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.4)' : 'rgba(21, 101, 192, 0.3)'}`,
                  opacity: 0.8,
                  cursor: 'not-allowed'
                }}
              >
                Reprendre
              </Button>
              <Button
                variant="text"
                startIcon={<FileUploadIcon />}
                sx={{
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: isDark ? '#4fc3f7' : '#1565c0',
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(21, 101, 192, 0.08)',
                  border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.4)' : 'rgba(21, 101, 192, 0.3)'}`,
                  opacity: 0.8,
                  cursor: 'not-allowed'
                }}
              >
                Importer depuis ISLP
              </Button>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Reprendre
            </Typography>
            <Typography variant="body1" paragraph>
              Cette fonction permet de reprendre les encodages et les rubriques d'un document existant dans BlueNote.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur « Reprendre », indiquez le numéro et l'année du document source, puis cochez les éléments que vous souhaitez récupérer : rédaction, service, intervenants, faits, personnes physiques, personnes morales, objets, véhicules ou rubriques.
            </Typography>
            <Typography variant="body1" paragraph>
              Après validation, les éléments sélectionnés sont ajoutés dans le document en cours.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Importer depuis ISLP
            </Typography>
            <Typography variant="body1" paragraph>
              Cette fonction permet d'utiliser des données brutes provenant de l'ISLP.
            </Typography>
            <Typography variant="body1" paragraph>
              Dans ISLP, copiez les données souhaitées depuis les onglets « Faits », « Personnes physiques », « Personnes morales », « Véhicules » ou « Objets », puis collez-les dans la fenêtre d'import de BlueNote.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez ensuite sur « Traduire les données » : BlueNote analyse le texte copié et le convertit en encodages utilisables dans l'application. Vous pouvez vérifier les éléments détectés avant de cliquer sur « Importer ».
            </Typography>
          </Box>

          <Box id="ajouter-rubrique" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Ajouter une rubrique
            </Typography>
            <Typography variant="body1" paragraph>
              BlueNote vous permet d'ajouter des rubriques à votre procès-verbal de quatre manières différentes :
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              1. Ajouter une rubrique vierge
            </Typography>
            <Typography variant="body1" paragraph>
              Rendez-vous dans l'onglet « Rubriques » (colonne de gauche)
            </Typography>
            <Typography variant="body1" paragraph>
              Dans la section « Ajouter une rubrique vierge », saisissez le titre de la rubrique souhaitée
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur l'icône « + » pour l'ajouter
            </Typography>
            <Typography variant="body1" paragraph>
              La rubrique est alors insérée dans le PV, sans contenu prérempli.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              2. Ajouter une rubrique générative
            </Typography>
            <Typography variant="body1" paragraph>
              Toujours dans l'onglet « Rubriques », accédez à la partie « Ajouter une rubrique générative »
            </Typography>
            <Typography variant="body1" paragraph>
              Une liste de modèles prédéfinis vous est proposée
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur l'icône « + » pour ajouter une rubrique issue de cette liste
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez filtrer les rubriques par catégorie ou effectuer une recherche par mot-clé pour gagner du temps.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              3. Ajouter un canevas de plusieurs rubriques
            </Typography>
            <Typography variant="body1" paragraph>
              Accédez à l'onglet « Canevas » (colonne de gauche)
            </Typography>
            <Typography variant="body1" paragraph>
              Une sélection de canevas complets vous est proposée, regroupant plusieurs rubriques cohérentes
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur l'icône « + » pour insérer l'ensemble des rubriques du canevas en une seule action
            </Typography>
            <Box id="rubriques">
              <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
                4. Utiliser mes rubriques personnelles
              </Typography>
              <Typography variant="body1" paragraph>
                Rendez-vous dans l'onglet « Rubriques » de la colonne de gauche, puis dans la section « Ajouter une rubrique générative ».
              </Typography>
              <Typography variant="body1" paragraph>
                Dans le menu déroulant des catégories, sélectionnez <strong>« Mes rubriques »</strong> pour afficher uniquement vos rubriques personnelles.
              </Typography>
              <Typography variant="body1" paragraph>
                Parcourez la liste, ou utilisez la barre de recherche pour retrouver rapidement la rubrique souhaitée.
              </Typography>
              <Typography variant="body1" paragraph>
                Cliquez sur l'icône « + » à côté de la rubrique pour l'insérer directement dans votre procès-verbal.
              </Typography>
              <Box sx={{ 
                p: 2.5, 
                bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
                mb: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
                boxShadow: isDark
                  ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                  : '0 2px 8px rgba(255, 193, 7, 0.08)'
              }}>
                <Box sx={{ 
                  fontSize: '1.5rem',
                  color: '#FFC107',
                  textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
                }}>
                  💡
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 'bold', 
                    color: isDark ? '#FFC107' : '#F57C00',
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}>
                    Astuce
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 500,
                    color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                    lineHeight: 1.4
                  }}>
                    Si la liste « Mes rubriques » est vide, vous devez d'abord créer vos rubriques personnelles depuis la page « Mes rubriques ». Pour en savoir plus, consultez la section «{' '}
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: isDark ? '#90caf9' : 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          opacity: 0.7
                        }
                      }}
                      onClick={() => scrollToSection('rubriques-perso')}
                    >
                      Créer, modifier et supprimer mes rubriques personnelles
                    </Typography>
                    {' '}».
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box id="deplacer-rubrique" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Déplacer une rubrique
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez réorganiser l'ordre des rubriques de votre procès-verbal de deux manières :
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              1. Déplacement via le mode Édition
            </Typography>
            <Typography variant="body1" paragraph>
              Assurez-vous d'être en mode Édition
            </Typography>
            <Typography variant="body1" paragraph>
              Chaque rubrique affiche alors deux flèches :
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur la flèche vers le haut pour faire monter la rubrique
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur la flèche vers le bas pour la faire descendre
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              2. Déplacement via le sommaire
            </Typography>
            <Typography variant="body1" paragraph>
              Ouvrez le sommaire situé dans la colonne de droite
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur l'icône « trois lignes » à côté de la rubrique souhaitée
            </Typography>
            <Typography variant="body1" paragraph>
              Glissez-déposez la rubrique à la position désirée
            </Typography>
          </Box>

          <Box id="supprimer-rubrique" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Supprimer une rubrique
            </Typography>
            <Typography variant="body1" paragraph>
              Pour supprimer une rubrique de votre procès-verbal :
            </Typography>
            <Typography variant="body1" paragraph>
              Passez en mode Édition
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur l'icône « poubelle » située à droite du titre de la rubrique concernée
            </Typography>
            <Typography variant="body1" paragraph>
              Une fenêtre de confirmation s'affichera : confirmez pour valider la suppression
            </Typography>
          </Box>

          <Box id="generer-rubrique" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Générer le contenu d'une rubrique
            </Typography>
            <Typography variant="body1" paragraph>
              Passez en mode Édition.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur le bouton rouge « Générer » situé sur la rubrique que vous souhaitez remplir.
            </Typography>
            <Typography variant="body1" paragraph>
              Si ce bouton est absent, cela signifie que la rubrique a déjà été générée.
            </Typography>
            <Typography variant="body1" paragraph>
              Remplissez le formulaire qui s'affiche avec les informations demandées.
            </Typography>
            <Typography variant="body1" paragraph>
              Une fois le formulaire complété, cliquez sur le bouton « Générer » en bas du formulaire.
            </Typography>
            <Typography variant="body1" paragraph>
              Votre rubrique est alors générée et prête à être modifiée librement.
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Bouton générer grisé
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Si le bouton « Générer » est grisé, cela indique qu'un autre utilisateur est en train de générer cette rubrique. Vous ne pourrez la générer tant que cette action est en cours.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box id="modifier-rubrique" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Modifier le texte et le contenu d'une rubrique
            </Typography>
            <Typography variant="body1" paragraph>
              Passez en mode Édition.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur le bouton vert « Modifier » de la rubrique que vous souhaitez éditer.
            </Typography>
            <Typography variant="body1" paragraph>
              Si ce bouton est absent, la rubrique doit d'abord être générée.
            </Typography>
            <Typography variant="body1" paragraph>
              Une fois le bouton cliqué, vous pouvez modifier le titre et le contenu de la rubrique grâce à un éditeur de texte intégré.
            </Typography>
            <Typography variant="body1" paragraph>
              Celui-ci offre diverses fonctionnalités, comme le gras, le souligné, l'italique et bien d'autres.
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Bouton modifier grisé
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Si le bouton « Modifier » est grisé, cela signifie qu'un autre utilisateur est en train de modifier cette rubrique. Vous ne pourrez pas y accéder tant que l'édition est en cours.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box id="commenter-rubrique" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Commenter une rubrique
            </Typography>
            <Typography variant="body1" paragraph>
              Chaque rubrique dispose d'un bouton en forme de haut-parleur qui permet d'afficher ou de masquer la zone de commentaires liée à cette rubrique.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 2 }}>
              <IconButton
                size="small"
                sx={{ 
                  color: 'white',
                  backgroundColor: isDark ? '#5dade2' : '#3498db',
                  '&:hover': {
                    backgroundColor: isDark ? '#2980b9' : '#2471a3',
                  },
                  width: '32px',
                  height: '32px',
                  opacity: 0.8,
                  cursor: 'not-allowed'
                }}
              >
                <CampaignIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body1" paragraph>
              Cliquez sur ce bouton pour ouvrir les commentaires de la rubrique. Le nombre affiché sur l'icône indique combien de commentaires existent déjà.
            </Typography>
            <Typography variant="body1" paragraph>
              Pour ajouter un commentaire, écrivez votre message dans le champ « Ajouter un commentaire... », puis cliquez sur l'icône d'envoi ou appuyez sur la touche Entrée.
            </Typography>
            <Typography variant="body1" paragraph>
              Vos propres commentaires peuvent ensuite être modifiés via l'icône <EditIcon sx={{ fontSize: '1rem' }} /> ou supprimés via l'icône <DeleteIcon sx={{ fontSize: '1rem' }} />.
            </Typography>
          </Box>

          <Box id="historique" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Consulter l'historique
            </Typography>
            <Typography variant="body1" paragraph>
              Le bouton « Historique » situé en haut du document permet d'accéder à la liste des modifications apportées aux rubriques.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 2 }}>
              <Button
                startIcon={<HistoryIcon />}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  height: '32px',
                  borderColor: isDark ? '#64b5f6' : 'primary.main',
                  color: isDark ? '#64b5f6' : 'primary.main',
                  backgroundColor: isDark ? 'rgba(100, 181, 246, 0.05)' : 'transparent',
                  opacity: 0.8,
                  cursor: 'not-allowed'
                }}
              >
                Historique
              </Button>
            </Box>
            <Typography variant="body1" paragraph>
              L'historique s'ouvre dans un panneau latéral. Les entrées y sont regroupées par date et indiquent le type d'action réalisée, par exemple une création, une modification, une génération, une suppression ou une restauration.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur une entrée pour afficher le détail de la modification. Vous pouvez alors comparer le contenu précédent avec le contenu actuel ou consulter la version enregistrée dans l'historique.
            </Typography>
            <Typography variant="body1" paragraph>
              Si vous souhaitez revenir à un ancien contenu, cliquez sur le bouton :
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
              <Button
                variant="contained"
                color="warning"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  opacity: 0.8,
                  cursor: 'not-allowed'
                }}
              >
                Restaurer cette version
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  opacity: 0.8,
                  cursor: 'not-allowed'
                }}
              >
                Récupérer cette rubrique
              </Button>
            </Box>
            <Typography variant="body1" paragraph>
              « Restaurer cette version » remplace le contenu actuel de la rubrique par celui de la version sélectionnée.
            </Typography>
            <Typography variant="body1" paragraph>
              « Récupérer cette rubrique » apparaît lorsqu'une rubrique a été supprimée. Il permet de réinsérer cette rubrique dans le document, avec son contenu historique.
            </Typography>
          </Box>

          <Box id="correcteur" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Utiliser le correcteur orthographique
            </Typography>
            <Typography variant="body1" paragraph>
              Pour vous aider à détecter les fautes dans vos rubriques, nous vous recommandons d'activer le correcteur orthographique intégré à votre navigateur. Voici comment faire pour les principaux navigateurs :
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Google Chrome
            </Typography>
            <Typography variant="body1" paragraph>
              Ouvrez Chrome sur votre ordinateur.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur « Plus » (les trois points verticaux en haut à droite).
            </Typography>
            <Typography variant="body1" paragraph>
              Sélectionnez « Paramètres » puis « Langues ».
            </Typography>
            <Typography variant="body1" paragraph>
              Sous Correcteur orthographique, activez l'option « Vérifier l'orthographe du texte saisi sur les pages Web ».
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Mozilla Firefox
            </Typography>
            <Typography variant="body1" paragraph>
              Par défaut, Firefox vérifie l'orthographe automatiquement. Si ce n'est pas le cas :
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur le bouton Menu (trois lignes horizontales).
            </Typography>
            <Typography variant="body1" paragraph>
              Sélectionnez « Paramètres ».
            </Typography>
            <Typography variant="body1" paragraph>
              Dans le panneau Général, descendez jusqu'à la section Langue.
            </Typography>
            <Typography variant="body1" paragraph>
              Cochez la case « Vérifier l'orthographe pendant la saisie ».
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Microsoft Edge
            </Typography>
            <Typography variant="body1" paragraph>
              Par défaut, Edge vérifie l'orthographe automatiquement. Sinon :
            </Typography>
            <Typography variant="body1" paragraph>
              Ouvrez Microsoft Edge.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur les trois points verticaux (menu) en haut à droite.
            </Typography>
            <Typography variant="body1" paragraph>
              Sélectionnez « Paramètres ».
            </Typography>
            <Typography variant="body1" paragraph>
              Dans la barre latérale gauche, cliquez sur « Langues ».
            </Typography>
            <Typography variant="body1" paragraph>
              Activez l'option « Vérifier l'orthographe ».
            </Typography>
            <Typography variant="body1" paragraph>
              Si vous le souhaitez, activez aussi « Correction automatique » pour que les fautes soient corrigées automatiquement.
            </Typography>
            <Typography variant="body1" paragraph>
              Sélectionnez les langues pour lesquelles vous souhaitez activer le correcteur.
            </Typography>
          </Box>

          <Box id="discussion" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Utiliser la discussion
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur le bouton « Discussion » dans la colonne de droite pour ouvrir la zone de discussion liée au procès-verbal.
            </Typography>
            <Typography variant="body1" paragraph>
              Pour envoyer un message, rédigez-le dans le champ prévu à cet effet, puis cliquez sur la flèche pour l'envoyer.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Important : il n'est pas possible de modifier ou supprimer un message après son envoi.
            </Typography>
            <Typography variant="body1" paragraph>
              Vous y trouverez également des informations sur les ajouts réalisés par les autres utilisateurs.
            </Typography>
          </Box>

          <Box id="guardia" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Utiliser GuardIA
            </Typography>
            <Typography variant="body1" paragraph>
              GuardIA se trouve dans la colonne de droite de l'application. Cliquez sur l'onglet « GuardIA », puis choisissez la fonctionnalité souhaitée dans le menu déroulant : « Description d'image », « Transcription audio » ou « Synthèse des faits ».
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
              <Chip 
                icon={<PhotoCameraIcon />}
                label="Description d'image"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: isDark ? '#c084fc' : '#800080',
                  fontWeight: 500,
                  border: `1px solid ${isDark ? '#c084fc' : '#800080'}`,
                  '& .MuiChip-icon': {
                    color: isDark ? '#c084fc' : '#800080'
                  }
                }}
              />
              <Chip 
                icon={<AudioFileIcon />}
                label="Transcription audio"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: isDark ? '#c084fc' : '#800080',
                  fontWeight: 500,
                  border: `1px solid ${isDark ? '#c084fc' : '#800080'}`,
                  '& .MuiChip-icon': {
                    color: isDark ? '#c084fc' : '#800080'
                  }
                }}
              />
              <Chip 
                icon={<SummarizeIcon />}
                label="Synthèse des faits"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: isDark ? '#c084fc' : '#800080',
                  fontWeight: 500,
                  border: `1px solid ${isDark ? '#c084fc' : '#800080'}`,
                  '& .MuiChip-icon': {
                    color: isDark ? '#c084fc' : '#800080'
                  }
                }}
              />
            </Box>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Description d'image
            </Typography>
            <Typography variant="body1" paragraph>
              Cette fonctionnalité permet de déposer une image, ou de la sélectionner depuis votre ordinateur, afin d'obtenir une proposition de description textuelle.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur « Envoyer » pour lancer la génération. Une fois la description proposée par GuardIA affichée, vous pouvez la copier et l'utiliser comme aide rédactionnelle.
            </Typography>
            <Typography variant="body1" paragraph>
              Cette description ne constitue pas une analyse technique, une expertise ou une validation des éléments représentés. Elle peut contenir des erreurs ou approximations, notamment sur l'identification d'objets, la lecture d'inscriptions, le contexte de la scène ou la nature exacte d'un élément visible.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Transcription audio
            </Typography>
            <Typography variant="body1" paragraph>
              Cette fonctionnalité permet de déposer un fichier audio, ou de le sélectionner depuis votre ordinateur, afin d'obtenir une proposition de retranscription textuelle.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur « Transcrire » pour lancer le traitement. La retranscription peut ensuite être copiée. Lorsque l'option est disponible, vous pouvez aussi afficher le texte par segments avec des timecodes.
            </Typography>
            <Typography variant="body1" paragraph>
              La retranscription peut contenir des erreurs, omissions ou confusions, notamment si l'enregistrement est de mauvaise qualité, s'il y a du bruit ambiant, plusieurs intervenants, une élocution rapide, des accents, ou des noms propres, dates, heures, lieux, numéros ou références difficiles à interpréter. Les timecodes éventuels sont uniquement indicatifs.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Synthèse des faits
            </Typography>
            <Typography variant="body1" paragraph>
              Cette fonctionnalité permet de coller une audition ou un texte long afin d'obtenir une proposition de relation des faits, rédigée dans un langage policier neutre et objectif.
            </Typography>
            <Typography variant="body1" paragraph>
              Choisissez le niveau de synthèse souhaité : « Détaillée » relate le déroulement des faits et les éléments utiles en un texte synthétisé, « Essentielle » résume les faits principaux en deux ou trois phrases et « Courte » résume l'ensemble en une seule phrase. Cliquez ensuite sur « Synthétiser » (ou utilisez Ctrl + Entrée), puis copiez le résultat à l'aide du bouton « Copier ».
            </Typography>
            <Typography variant="body1" paragraph>
              La synthèse repose uniquement sur le texte fourni. Elle peut néanmoins omettre un élément, en reformuler un autre de manière imprécise ou mal attribuer un propos : vérifiez-la toujours au regard du texte source.
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Règles de sécurité
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4,
                  mb: 1
                }}>
                  Les contenus générés par GuardIA sont des propositions d'aide à la rédaction. Ils doivent toujours être relus, vérifiés et adaptés par l'utilisateur avant d'être repris dans un document.
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4,
                  mb: 1
                }}>
                  GuardIA ne remplace ni l'écoute directe d'un fichier audio lorsque celle-ci est nécessaire, ni l'examen direct d'une image, ni la lecture intégrale du texte source par l'utilisateur.
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Pour consulter le cadre complet d'utilisation, rendez-vous dans les «{' '}
                  <Typography
                    component="span"
                    sx={{
                      color: isDark ? '#90caf9' : 'primary.main',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': {
                        opacity: 0.7
                      }
                    }}
                    onClick={() => {
                      sessionStorage.setItem('cguPreviousPage', 'Tutorial');
                      setPage('CGU');
                    }}
                  >
                    Conditions d'utilisation GuardIA
                  </Typography>
                  {' '}».
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box id="insertions" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Utiliser mes insertions personnelles
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur le bouton « Insertions » dans la colonne de droite pour accéder à la liste de vos insertions.
            </Typography>
            <Typography variant="body1" paragraph>
              Parcourez la liste et sélectionnez l'insertion souhaitée.
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur l'icône « Copier » : le contenu de l'insertion est alors copié dans votre presse-papiers.
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez ensuite coller ce contenu dans la rubrique de votre choix.
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez également utiliser une insertion automatique directement dans l'éditeur : le nom d'insertion fait office de raccourci. Tapez ce nom, puis appuyez sur la touche <strong>F3</strong> ; il est alors remplacé par le contenu de l'insertion correspondante.
            </Typography>
          </Box>

          <Box id="annexes" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Générer une annexe
            </Typography>
            <Typography variant="body1" paragraph>
              Les annexes se trouvent dans l'onglet « Annexes » de la colonne de gauche.
            </Typography>
            <Typography variant="body1" paragraph>
              Sélectionnez le type d'annexe souhaité dans le menu déroulant « Type d'annexe ». Selon le choix effectué, BlueNote propose un outil adapté : dossier photographique, formulaire à compléter ou document directement téléchargeable.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Dossier photographique
            </Typography>
            <Typography variant="body1" paragraph>
              Sélectionnez « Dossier photographique », puis cliquez sur « Ajouter des photos » pour sélectionner les images depuis les dossiers de votre ordinateur.
            </Typography>
            <Typography variant="body1" paragraph>
              Vos images s'affichent alors en liste. Vous pouvez réorganiser leur ordre avec les flèches, modifier leur titre via l'icône <EditIcon sx={{ fontSize: '1rem' }} />, choisir leur orientation ou supprimer une image via l'icône « poubelle ».
            </Typography>
            <Typography variant="body1" paragraph>
              Choisissez le nombre d'images à afficher par page, puis cliquez sur « Exporter en PDF » pour générer un fichier .PDF regroupant toutes les images.
            </Typography>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Images non sauvegardées
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4,
                  mb: 1
                }}>
                  Dans le dossier photographique, les images importées ne sont pas sauvegardées dans l'application. Elles sont conservées uniquement en local pendant la session.
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4,
                  mb: 1
                }}>
                  Si vous quittez cet onglet ou actualisez la page, toutes vos modifications seront perdues.
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  Nous vous recommandons donc de créer votre dossier photographique en une seule fois, avant de quitter l'onglet ou d'actualiser la page.
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Documents générés à partir d'un formulaire
            </Typography>
            <Typography variant="body1" paragraph>
              Certains types d'annexes affichent un formulaire à compléter. C'est le cas, par exemple, des droits Salduz, d'une attestation, d'une déclaration écrite ou d'une renonciation à un avocat.
            </Typography>
            <Typography variant="body1" paragraph>
              Remplissez les informations demandées, puis cliquez sur le bouton de génération ou de téléchargement proposé. BlueNote utilise alors les données du formulaire, et parfois celles déjà encodées dans le document, pour produire l'annexe au format prévu.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Documents téléchargeables
            </Typography>
            <Typography variant="body1" paragraph>
              La rubrique « Documents téléchargeables » donne accès à des documents mis à disposition dans l'application. Ces fichiers ne sont pas générés à partir du document en cours : ils sont simplement disponibles au téléchargement.
            </Typography>
            <Typography variant="body1" paragraph>
              Utilisez le champ de recherche pour retrouver un document par son titre, puis cliquez sur la ligne correspondante pour le télécharger.
            </Typography>
          </Box>

          <Box id="sauvegarder" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Sauvegarder mon document
            </Typography>
            <Typography variant="body1" paragraph>
              Il n'est pas nécessaire de sauvegarder votre document manuellement, cela se fait automatiquement.
            </Typography>
          </Box>

          <Box id="supprimer-pv" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Supprimer un document
            </Typography>
            <Typography variant="body1" paragraph>
              Pour supprimer un document, cliquez simplement sur ce bouton situé en haut à droite de la page :
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 2 }}>
              <Button
                startIcon={<DeleteIcon />}
                variant="outlined"
                size="small"
                disabled
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  height: '32px',
                  borderColor: isDark ? '#f44336' : 'error.main',
                  color: isDark ? '#f44336' : 'error.main',
                  backgroundColor: isDark ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                  '&.Mui-disabled': {
                    borderColor: isDark ? '#f44336' : 'error.main',
                    color: isDark ? '#f44336' : 'error.main',
                    opacity: 0.7,
                  }
                }}
              >
                Supprimer
              </Button>
            </Box>
            <Box sx={{ 
              p: 2.5, 
              bgcolor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.08)',
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,193,7,0.08) 0%, rgba(255,193,7,0.03) 100%)',
              boxShadow: isDark
                ? '0 2px 8px rgba(255, 193, 7, 0.1)'
                : '0 2px 8px rgba(255, 193, 7, 0.08)'
            }}>
              <Box sx={{ 
                fontSize: '1.5rem',
                color: '#FFC107',
                textShadow: isDark ? '0 0 4px rgba(255, 193, 7, 0.3)' : 'none'
              }}>
                ⚠️
              </Box>
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: isDark ? '#FFC107' : '#F57C00',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem'
                }}>
                  Attention
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                  lineHeight: 1.4
                }}>
                  La suppression d'un document n'est pas autorisée pour les utilisateurs. Si vous estimez qu'un document doit être supprimé, veuillez contacter le gestionnaire de l'application.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box id="exporter" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Exporter mon document
            </Typography>
            <Typography variant="body1" paragraph>
              BlueNote vous offre une option pour exporter votre document :
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              Télécharger :
            </Typography>
            <Typography variant="body1" paragraph>
              Cliquez sur ce bouton pour enregistrer votre document au format .DOCX :
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 2 }}>
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                disabled
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  height: '32px',
                  borderColor: isDark ? '#66bb6a' : 'success.main',
                  color: isDark ? '#66bb6a' : 'success.main',
                  backgroundColor: isDark ? 'rgba(102, 187, 106, 0.05)' : 'transparent',
                  opacity: 0.7,
                  cursor: 'not-allowed',
                  '&.Mui-disabled': {
                    borderColor: isDark ? '#66bb6a' : 'success.main',
                    color: isDark ? '#66bb6a' : 'success.main',
                    opacity: 0.7,
                  }
                }}
              >
                Télécharger
              </Button>
            </Box>
            <Typography variant="body1" paragraph>
              Cette exportation inclut également l'en-tête du document, ce qui vous permet de l'imprimer directement.
            </Typography>
            <Typography variant="body1" paragraph>
              Le fichier est compatible avec Microsoft Word à partir de la version 2007.
            </Typography>
            <Typography variant="body1" paragraph>
              Pour les versions antérieures, une conversion au format .DOC est nécessaire, ce qui est déconseillé pour conserver la mise en forme.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box id="renseignements" sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: isDark ? '#90caf9' : 'primary.main', mb: 3 }}>
            Renseignements complémentaires
          </Typography>
          
          <Box id="deconnexion" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Se déconnecter
            </Typography>
            <Typography variant="body1" paragraph sx={{ display: 'inline' }}>
              Pour vous déconnecter de l'application, cliquez sur le menu déroulant en haut, puis sélectionnez 
              <Chip 
                icon={<LogoutIcon />}
                label="Déconnexion"
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: '#F44336',
                  fontWeight: 500,
                  border: '1px solid #F44336',
                  mx: 0.5,
                  '& .MuiChip-icon': {
                    color: '#F44336'
                  }
                }}
              />
              .
            </Typography>
          </Box>

          <Box id="securite" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Sécurité
            </Typography>
            <Typography variant="body1" paragraph>
              Votre application BlueNote fonctionne exclusivement au sein de votre réseau local sécurisé.
            </Typography>
            <Typography variant="body1" paragraph>
              Toutes les données que vous saisissez sont stockées uniquement dans votre zone de police.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong><i>pyll</i> SRL</strong>, ainsi que toute autre personne extérieure à votre zone, n'ont aucun accès à ces données.
            </Typography>
          </Box>

          <Box id="contact" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Contact et signalement
            </Typography>
            <Typography variant="body1" paragraph>
              Vous souhaitez signaler un <strong>défaut</strong>, proposer une <strong>amélioration</strong>, poser une <strong>question</strong> ou simplement entrer en contact avec les développeurs de BlueNote ?
            </Typography>
            <Box sx={{ 
              p: 3, 
              bgcolor: isDark ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.08)',
              borderRadius: 3,
              border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
              mb: 2,
              textAlign: 'center',
              background: isDark 
                ? 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(76,175,80,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(76,175,80,0.08) 0%, rgba(76,175,80,0.03) 100%)',
              boxShadow: isDark
                ? '0 4px 12px rgba(76, 175, 80, 0.15)'
                : '0 4px 12px rgba(76, 175, 80, 0.1)'
            }}>
              <Box sx={{ 
                fontSize: '2rem',
                color: '#4CAF50',
                mb: 1.5,
                textShadow: isDark ? '0 0 8px rgba(76, 175, 80, 0.3)' : 'none'
              }}>
                📧
              </Box>
              <Typography variant="body1" sx={{ 
                color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                mb: 1,
                fontSize: '0.95rem'
              }}>
                Envoyez-nous un courrier électronique à :
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold',
                color: isDark ? '#4CAF50' : '#2E7D32',
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
                textShadow: isDark ? '0 1px 2px rgba(76, 175, 80, 0.2)' : 'none'
              }}>
                contact@pyll.be
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Pour nous aider à vous répondre rapidement, merci d'indiquer la version de BlueNote que vous utilisez, soit : « <i> v <SettingsFieldDisplay fieldName="version" /> – <SettingsFieldDisplay fieldName="clientNumber" /> </i> ».
            </Typography>
          </Box>

          <Box id="version" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Version de l'application
            </Typography>
            <Typography variant="body1" paragraph>
              Vous utilisez la version <i><SettingsFieldDisplay fieldName="version" /> - <SettingsFieldDisplay fieldName="clientNumber" /></i> de l'application BlueNote, développée par <strong><i>pyll</i> SRL</strong> à destination de la <i><SettingsFieldDisplay fieldName="clientName" /></i>.
            </Typography>
          </Box>

          <Box id="conditions" sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#81c784' : 'secondary.main', mb: 2 }}>
              Conditions d'utilisation et mentions légales
            </Typography>
            <Typography variant="body1" paragraph>
              Pour consulter les conditions d'utilisation et les mentions légales de l'application BlueNote, cliquez sur «{' '}
              <Typography
                component="span"
                sx={{
                  color: isDark ? '#90caf9' : 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
                onClick={() => {
                  sessionStorage.setItem('cguPreviousPage', 'Tutorial');
                  setPage('CGU');
                }}
              >
                Conditions d'utilisation et mentions légales
              </Typography>
              {' '}».
            </Typography>
          </Box>
        </Box>

      </CardContent>
    </Box>
   </Box>
   )
}

export default Tutorial
